import * as Sentry from '@sentry/react';
import { useCallback } from 'react';

/**
 * Custom hook for Sentry error tracking and user context management
 */
export const useSentry = () => {
  /**
   * Capture an exception with additional context
   */
  const captureException = useCallback((
    error: Error,
    context?: {
      tags?: Record<string, string>;
      extra?: Record<string, unknown>;
      user?: Sentry.User;
      level?: Sentry.SeverityLevel;
    }
  ) => {
    Sentry.captureException(error, {
      tags: context?.tags,
      extra: context?.extra,
      user: context?.user,
      level: context?.level || 'error',
    });
  }, [])

  /**
   * Capture a message with additional context
   */
  const captureMessage = useCallback((
    message: string,
    level: Sentry.SeverityLevel = 'info',
    context?: {
      tags?: Record<string, string>;
      extra?: Record<string, unknown>;
    }
  ) => {
    Sentry.captureMessage(message, (scope) => {
      if (context?.tags) {
        Object.entries(context.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }
      if (context?.extra) {
        Object.entries(context.extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      scope.setLevel(level);
      
      return scope;
    });
  }, [])

  /**
   * Set user context for current session
   */
  const setUser = useCallback((user: Sentry.User) => {
    Sentry.setUser(user);
  }, [])

  /**
   * Add breadcrumb for debugging
   */
  const addBreadcrumb = useCallback((message: string, category?: string, data?: Record<string, unknown>) => {
    Sentry.addBreadcrumb({
      message,
      category: category || 'custom',
      data,
      level: 'info',
    });
  }, [])

  /**
   * Set tag for current session
   */
  const setTag = useCallback((key: string, value: string) => {
    Sentry.setTag(key, value);
  }, [])

  /**
   * Set extra data for current session
   */
  const setExtra = useCallback((key: string, value: unknown) => {
    Sentry.setExtra(key, value);
  }, [])

  return {
    captureException,
    captureMessage,
    setUser,
    addBreadcrumb,
    setTag,
    setExtra,
  };
};
